import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'flowbite-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SparklesIcon, CalendarDaysIcon } from 'lucide-react';
import { useVersion } from '../contexts/VersionContext';
import { ChangelogService, comparaVersiuni } from '../services/ChangelogService';
import { ChangelogEntry } from '../interfaces/Changelog';

/**
 * Maparea markdown → JSX. Proiectul nu are pluginul `@tailwindcss/typography`, deci stilurile
 * sunt explicite aici. Documentația versiunilor se scrie pentru utilizatori, așa că accentul e
 * pe lizibilitate: titluri clare, spațiere generoasă, imagini mari.
 */
const componenteMarkdown = {
  h1: ({ node, ...props }: any) => (
    <h1 className="mb-3 mt-6 text-lg font-bold text-gray-900 first:mt-0 dark:text-dm-text" {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <h2
      className="mb-2.5 mt-7 border-l-[3px] border-primary pl-2.5 text-[15px] font-bold text-gray-900 first:mt-0 dark:text-dm-text"
      {...props}
    />
  ),
  h3: ({ node, ...props }: any) => (
    <h3 className="mb-1.5 mt-5 text-sm font-semibold text-gray-800 first:mt-0 dark:text-dm-text-secondary" {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <p className="mb-3 text-sm leading-relaxed text-gray-700 dark:text-dm-text-secondary" {...props} />
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="mb-3 ml-5 list-disc space-y-1.5 text-sm text-gray-700 dark:text-dm-text-secondary" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="mb-3 ml-5 list-decimal space-y-1.5 text-sm text-gray-700 dark:text-dm-text-secondary" {...props} />
  ),
  li: ({ node, ...props }: any) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }: any) => (
    <strong className="font-semibold text-gray-900 dark:text-dm-text" {...props} />
  ),
  a: ({ node, ...props }: any) => (
    <a
      className="font-medium text-darkprimary underline dark:text-primary"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  img: ({ node, ...props }: any) => (
    <img
      className="my-4 max-w-full rounded-xl border border-gray-200 shadow-sm dark:border-dm-border"
      loading="lazy"
      {...props}
    />
  ),
  code: ({ node, inline, ...props }: any) =>
    inline ? (
      <code
        className="rounded bg-gray-100 px-1.5 py-0.5 text-[12px] text-gray-800 dark:bg-white/10 dark:text-dm-text"
        {...props}
      />
    ) : (
      <code className="block whitespace-pre-wrap break-words text-[12px]" {...props} />
    ),
  pre: ({ node, ...props }: any) => (
    <pre
      className="mb-3 overflow-x-auto rounded-xl bg-gray-100 p-3 text-gray-800 dark:bg-white/[0.06] dark:text-dm-text"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }: any) => (
    <blockquote
      className="mb-3 border-l-[3px] border-gray-300 pl-3 text-sm italic text-gray-600 dark:border-dm-border dark:text-dm-text-muted"
      {...props}
    />
  ),
  hr: ({ node, ...props }: any) => <hr className="my-6 border-gray-200 dark:border-dm-border" {...props} />,
  table: ({ node, ...props }: any) => (
    <div className="mb-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-dm-border">
      <table className="w-full text-left text-sm" {...props} />
    </div>
  ),
  th: ({ node, ...props }: any) => (
    <th
      className="whitespace-nowrap bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:bg-white/[0.04] dark:text-dm-text-muted"
      {...props}
    />
  ),
  td: ({ node, ...props }: any) => (
    <td
      className="border-t border-gray-100 px-3 py-2 align-top text-gray-700 dark:border-dm-border dark:text-dm-text-secondary"
      {...props}
    />
  ),
};

/** Titlul versiunii apare deja în antetul modalului — scoatem primul `# ...` ca să nu se repete. */
const scoateTitlulPrincipal = (markdown: string): string =>
  markdown.replace(/^\s*#\s+.*\r?\n/, '');

const formateazaData = (iso: string): string => {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * Istoricul versiunilor: lista în stânga, documentația versiunii selectate în dreapta.
 * Se deschide din bulina de status din bara de sus sau din popup-ul de versiune nouă.
 */
const ChangelogModal: React.FC = () => {
  const { changelogOpen, closeChangelog, currentVersion, latestVersion } = useVersion();

  const [versiuni, setVersiuni] = useState<ChangelogEntry[]>([]);
  const [selectata, setSelectata] = useState<string | null>(null);
  const [doc, setDoc] = useState<string>('');
  const [incarcaLista, setIncarcaLista] = useState(false);
  const [incarcaDoc, setIncarcaDoc] = useState(false);

  // Lista se cere la fiecare deschidere; serviciul are cache, deci nu costă decât prima dată.
  useEffect(() => {
    if (!changelogOpen) return;

    let anulat = false;
    setIncarcaLista(true);

    ChangelogService.getIndex().then((lista) => {
      if (anulat) return;
      setVersiuni(lista);
      setIncarcaLista(false);
      // Deschidem pe cea mai nouă versiune documentată, ca utilizatorul să vadă întâi noutățile.
      setSelectata((anterioara) => anterioara ?? lista[0]?.version ?? null);
    });

    return () => {
      anulat = true;
    };
  }, [changelogOpen]);

  const intrareSelectata = useMemo(
    () => versiuni.find((v) => v.version === selectata) ?? null,
    [versiuni, selectata],
  );

  useEffect(() => {
    if (!intrareSelectata) {
      setDoc('');
      return;
    }

    let anulat = false;
    setIncarcaDoc(true);

    ChangelogService.getDoc(intrareSelectata).then((text) => {
      if (anulat) return;
      setDoc(text);
      setIncarcaDoc(false);
    });

    return () => {
      anulat = true;
    };
  }, [intrareSelectata]);

  if (!changelogOpen) return null;

  const esteMaiNouaDecatALui = (versiune: string) => comparaVersiuni(versiune, currentVersion) > 0;

  return (
    <Modal show={changelogOpen} onClose={closeChangelog} size="6xl">
      <Modal.Header className="border-b border-gray-200 dark:border-dm-border">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-darkprimary dark:text-primary">
            <SparklesIcon className="h-5 w-5" />
          </span>
          <div>
            <div className="text-base font-bold text-gray-900 dark:text-dm-text">Noutăți</div>
            <div className="text-xs font-normal text-gray-500 dark:text-dm-text-muted">
              Folosești versiunea {currentVersion}
              {latestVersion && latestVersion !== currentVersion && ` · disponibilă ${latestVersion}`}
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="flex max-h-[70vh] flex-col sm:flex-row">
          {/* Lista versiunilor — coloană pe desktop, bandă orizontală pe telefon. */}
          <div className="shrink-0 border-b border-gray-200 dark:border-dm-border sm:w-64 sm:border-b-0 sm:border-r">
            <div className="flex gap-2 overflow-x-auto p-3 sm:max-h-[70vh] sm:flex-col sm:overflow-y-auto sm:overflow-x-hidden">
              {incarcaLista &&
                [0, 1, 2].map((i) => (
                  <div key={i} className="h-16 w-40 shrink-0 animate-pulse rounded-xl bg-gray-100 dark:bg-white/[0.06] sm:w-full" />
                ))}

              {!incarcaLista && versiuni.length === 0 && (
                <div className="px-1 py-2 text-sm text-gray-500 dark:text-dm-text-muted">
                  Nu există încă versiuni documentate.
                </div>
              )}

              {versiuni.map((v) => {
                const activa = v.version === selectata;
                return (
                  <button
                    key={v.version}
                    type="button"
                    onClick={() => setSelectata(v.version)}
                    className={`w-40 shrink-0 rounded-xl border p-2.5 text-left transition-all sm:w-full ${
                      activa
                        ? 'border-primary/50 bg-primary/10 dark:bg-primary/10'
                        : 'border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-dm-border dark:hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`text-sm font-bold ${
                          activa ? 'text-darkprimary dark:text-primary' : 'text-gray-800 dark:text-dm-text'
                        }`}
                      >
                        v{v.version}
                      </span>
                      {v.version === currentVersion && (
                        <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                          a ta
                        </span>
                      )}
                      {esteMaiNouaDecatALui(v.version) && (
                        <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                          nou
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-500 dark:text-dm-text-muted">
                      {v.title}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Documentația versiunii selectate. */}
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-5 sm:max-h-[70vh]">
            {intrareSelectata && (
              <div className="mb-5 border-b border-gray-100 pb-4 dark:border-dm-border">
                <h2 className="text-lg font-bold text-gray-900 dark:text-dm-text">{intrareSelectata.title}</h2>
                <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-dm-text-muted">
                  <CalendarDaysIcon className="h-3.5 w-3.5" />
                  {formateazaData(intrareSelectata.date)}
                </div>
              </div>
            )}

            {incarcaDoc && (
              <div className="space-y-2.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-3.5 animate-pulse rounded bg-gray-100 dark:bg-white/[0.06]"
                    style={{ width: `${100 - i * 8}%` }}
                  />
                ))}
              </div>
            )}

            {!incarcaDoc && intrareSelectata && !doc && (
              <p className="text-sm text-gray-500 dark:text-dm-text-muted">
                Documentația acestei versiuni nu este disponibilă.
              </p>
            )}

            {!incarcaDoc && doc && (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={componenteMarkdown}>
                {scoateTitlulPrincipal(doc)}
              </ReactMarkdown>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ChangelogModal;
